import PropTypes from "prop-types"
import React from "react"

import {
  boatSummary,
  customerValue,
  locationValue,
} from "./printIdentityValues"

const LABOR_PRODUCT_TYPE = "labor"
const BLANK_ROWS = [0, 1]

const CheckboxCell = () => (
  <div className="text-center">
    <span className="inline-block h-4 w-4 rounded-sm border-[1.5px] border-gray-400" />
  </div>
)

// break-after-avoid keeps a label glued to the row(s) it introduces, so it
// never strands alone at the bottom of a page when a long job flows over.
const SubLabel = ({ children }) => (
  <div className="mt-1.5 break-after-avoid pl-[34px] text-xxs font-semibold uppercase tracking-wide text-gray-400">
    {children}
  </div>
)

SubLabel.propTypes = { children: PropTypes.node }

const ItemRow = ({ name, quantity }) => (
  <div className="grid break-inside-avoid grid-cols-[34px_1fr_56px] items-center border-b border-gray-100 py-1.5 text-sm">
    <CheckboxCell />
    <div>{name}</div>
    <div className="font-mono text-center text-gray-700">
      {parseFloat(quantity)}
    </div>
  </div>
)

ItemRow.propTypes = {
  name: PropTypes.string,
  quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

const BlankRow = () => (
  <div className="grid break-inside-avoid grid-cols-[34px_1fr_56px] items-center gap-x-3 border-b border-gray-100 py-1.5">
    <CheckboxCell />
    <div className="h-4 border-b border-dashed border-gray-300" />
    <div className="h-4 border-b border-dashed border-gray-300" />
  </div>
)

// One checklist per item type. Requested items the SM entered are separated
// from the blank rows the tech hand-writes into, so neither reads as the other.
// The section itself is allowed to break across pages (a long parts list flows
// rather than bumping as a block and leaving a gap); only its rows stay whole.
const ChecklistSection = ({ label, items }) => (
  <div className="mt-2 first:mt-0">
    <div className="grid break-after-avoid grid-cols-[34px_1fr_56px] border-b border-gray-200 py-1.5 text-xxs font-bold uppercase tracking-wide text-gray-500">
      <div />
      <div>{label}</div>
      <div className="text-center">Qty</div>
    </div>
    {items.length > 0 && (
      <>
        <SubLabel>Requested</SubLabel>
        {items.map((lineItem) => (
          <ItemRow
            key={lineItem.id}
            name={lineItem.name}
            quantity={lineItem.quantity}
          />
        ))}
      </>
    )}
    <SubLabel>Technician added</SubLabel>
    {BLANK_ROWS.map((row) => (
      <BlankRow key={row} />
    ))}
  </div>
)

ChecklistSection.propTypes = {
  label: PropTypes.string.isRequired,
  items: PropTypes.array.isRequired,
}

const Field = ({ label, value }) => (
  <div>
    <div className="text-xxs font-bold uppercase tracking-wide text-gray-500">
      {label}
    </div>
    <div className="mt-0.5 font-semibold">{value || "—"}</div>
  </div>
)

Field.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
}

// Compact identity band repeated at the top of every job page, so each page is
// a self-contained job sheet that can be printed or handed out on its own.
const WorksheetPageHeader = ({ marina, workOrder, contactBoat }) => {
  const makeModelYear = [
    contactBoat?.make,
    contactBoat?.model,
    contactBoat?.year,
  ]
    .filter(Boolean)
    .join(" · ")

  return (
    <div className="break-inside-avoid">
      <div className="flex items-baseline justify-between border-b-2 border-gray-900 pb-1.5">
        <div className="flex items-baseline gap-2">
          <span className="text-base font-bold tracking-wide">
            TECH WORKSHEET
          </span>
          <span className="text-xxs text-gray-500">
            {marina.name} · Service department
          </span>
        </div>
        <div className="font-mono text-sm font-semibold">
          {workOrder.displaySequentialId}
        </div>
      </div>

      <div className="mt-2 grid grid-cols-3 gap-x-6 gap-y-1.5 text-xs">
        <Field label="Customer" value={customerValue(workOrder)} />
        <Field
          label="Boat"
          value={boatSummary(contactBoat, workOrder.boatLengthFormatted)}
        />
        <Field
          label="Location"
          value={locationValue(workOrder.assignedSpaceName)}
        />
        <Field label="Make · Model · Year" value={makeModelYear} />
        <Field label="Reg. Number" value={contactBoat?.registrationNumber} />
        <Field
          label="Assigned technician"
          value={
            <span className="mt-1 inline-block h-4 w-full max-w-[160px] border-b-[1.5px] border-gray-400 align-bottom" />
          }
        />
        <Field label="Hull ID" value={contactBoat?.hullIdentification} />
      </div>

      {workOrder.requestDescription && (
        <div className="mt-2 text-xs">
          <span className="text-xxs font-bold uppercase tracking-wide text-gray-500">
            Request:{" "}
          </span>
          <span className="whitespace-pre-line break-words text-gray-700">
            {workOrder.requestDescription}
          </span>
        </div>
      )}
    </div>
  )
}

WorksheetPageHeader.propTypes = {
  marina: PropTypes.shape({ name: PropTypes.string }).isRequired,
  workOrder: PropTypes.shape({
    displaySequentialId: PropTypes.string,
    requestDescription: PropTypes.string,
    contactName: PropTypes.string,
    contactPhone: PropTypes.string,
    contactEmail: PropTypes.string,
    assignedSpaceName: PropTypes.string,
    boatLengthFormatted: PropTypes.string,
  }).isRequired,
  contactBoat: PropTypes.shape({
    name: PropTypes.string,
    make: PropTypes.string,
    model: PropTypes.string,
    year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    registrationNumber: PropTypes.string,
    hullIdentification: PropTypes.string,
  }),
}

const TIME_LOG_COLUMNS = ["Date", "Start", "End", "Hours", "Tech initials"]

const TechWorksheetDocument = ({ printView }) => {
  const { marina, workOrder, contactBoat, jobs } = printView

  return (
    <div className="p-2 text-gray-900">
      {jobs.length === 0 && (
        <WorksheetPageHeader
          marina={marina}
          workOrder={workOrder}
          contactBoat={contactBoat}
        />
      )}
      {jobs.map((job, index) => {
        const lineItems = job.lineItems || []
        const laborItems = lineItems.filter(
          (lineItem) => lineItem.productType === LABOR_PRODUCT_TYPE
        )
        const partItems = lineItems.filter(
          (lineItem) => lineItem.productType !== LABOR_PRODUCT_TYPE
        )

        return (
          // Each job is its own self-identifying page: the header repeats and
          // jobs after the first start on a fresh page, so a single job sheet
          // can be printed or handed off on its own.
          <div
            key={job.id}
            className={index > 0 ? "break-before-page pt-2" : undefined}
          >
            <WorksheetPageHeader
              marina={marina}
              workOrder={workOrder}
              contactBoat={contactBoat}
            />

            <div className="mt-3 break-inside-avoid">
              <div className="text-xxs font-bold uppercase tracking-wide text-gray-400">
                Job {index + 1} of {jobs.length}
              </div>
              <div className="text-base font-bold">{job.name}</div>
              {job.description && (
                <div className="mt-0.5 whitespace-pre-line break-words text-xs leading-normal text-gray-600">
                  {job.description}
                </div>
              )}
            </div>

            <div className="mt-2">
              <ChecklistSection label="Parts" items={partItems} />
              <ChecklistSection label="Labor" items={laborItems} />

              <div className="mt-3 break-inside-avoid">
                <div className="mb-1.5 text-xxs font-bold uppercase tracking-wide text-gray-500">
                  Findings / notes
                </div>
                <div className="rounded-md h-[130px] border border-gray-300 bg-[repeating-linear-gradient(transparent,transparent_25px,#eef2f6_25px,#eef2f6_26px)]" />
              </div>

              <div className="mt-3.5 break-inside-avoid">
                <div className="mb-1.5 text-xxs font-bold uppercase tracking-wide text-gray-500">
                  Time log
                </div>
                <div className="rounded-t-md grid grid-cols-[1.2fr_1fr_1fr_0.8fr_1fr] border border-b-0 border-gray-300 text-xxs font-bold uppercase tracking-wide text-gray-500">
                  {TIME_LOG_COLUMNS.map((column) => (
                    <div
                      key={column}
                      className="border-r border-gray-100 px-2.5 py-1.5 last:border-r-0"
                    >
                      {column}
                    </div>
                  ))}
                </div>
                <div className="rounded-b-md border border-gray-300">
                  {[0, 1, 2].map((row) => (
                    <div
                      key={row}
                      className="grid h-6 grid-cols-[1.2fr_1fr_1fr_0.8fr_1fr] border-t border-gray-100 first:border-t-0"
                    >
                      {TIME_LOG_COLUMNS.map((column) => (
                        <div
                          key={column}
                          className="border-r border-gray-100 last:border-r-0"
                        />
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

TechWorksheetDocument.propTypes = {
  printView: PropTypes.shape({
    marina: PropTypes.shape({
      name: PropTypes.string,
    }).isRequired,
    workOrder: PropTypes.shape({
      displaySequentialId: PropTypes.string,
      requestDescription: PropTypes.string,
      contactName: PropTypes.string,
      contactPhone: PropTypes.string,
      contactEmail: PropTypes.string,
      assignedSpaceName: PropTypes.string,
      boatLengthFormatted: PropTypes.string,
    }).isRequired,
    contactBoat: PropTypes.shape({
      name: PropTypes.string,
      make: PropTypes.string,
      model: PropTypes.string,
      year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      registrationNumber: PropTypes.string,
      hullIdentification: PropTypes.string,
    }),
    jobs: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
        name: PropTypes.string,
        description: PropTypes.string,
        status: PropTypes.string,
        lineItems: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            name: PropTypes.string,
            quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            productType: PropTypes.string,
          })
        ),
      })
    ).isRequired,
  }).isRequired,
}

export default TechWorksheetDocument
